// needs to match cache name configured in the pwa section of vue.config.js:
import ISlideView from '@app/types/ISlideView';
import PowerPointPresentation from '@app/services/export/ppt/PowerPointPresentation';
import ISlideData from '@app/types/data/slides/ISlideData';
import PowerPointSlideProcessor from '@app/services/export/ppt/slides/PowerPointSlideProcessor';
import PowerPointHomeSlideProcessor from '@app/services/export/ppt/slides/PowerPointHomeSlideProcessor';
import PowerPointTrainRouteSlideProcessor from '@app/services/export/ppt/slides/PowerPointTrainRouteSlideProcessor';
import PowerPointHighlightSlideProcessor from '@app/services/export/ppt/slides/PowerPointHighlightSlideProcessor';
import PowerPointSlideProcessorStub from '@app/services/export/ppt/slides/PowerPointSlideProcessorStub';
import PowerPointWelcomeSlideProcessor from '@app/services/export/ppt/slides/PowerPointWelcomeSlideProcessor';
import PowerPointSingleMediaSlideProcessor from '@app/services/export/ppt/slides/PowerPointSingleMediaSlideProcessor';
import PowerPointCooperationSlideProcessor from '@app/services/export/ppt/slides/PowerPointCooperationSlideProcessor';
import { presentationDimensions } from '@app/services/export/ppt/variables/constants';
import PowerPointThankYouSlideProcessor from '@app/services/export/ppt/slides/PowerPointThankYouSlideProcessor';
import PowerPointCustomSlideProcessor from '@app/services/export/ppt/slides/PowerPointCustomSlideProcessor';
import PowerPointSkiRegionSlideProcessor from '@app/services/export/ppt/slides/PowerPointSkiRegionSlideProcessor';
import PowerPointTravelPassSlideProcessor from '@app/services/export/ppt/slides/PowerPointTravelPassSlideProcessor';
import PowerPointTravelPassVipSlideProcessor
	from '@app/services/export/ppt/slides/PowerPointTravelPassVipSlideProcessor';
import PowerPointRoutePricesSlideProcessor from '@app/services/export/ppt/slides/PowerPointRoutePricesSlideProcessor';
import PowerPointOfferASlideProcessor from '@app/services/export/ppt/slides/PowerPointOfferASlideProcessor';
import PowerPointOfferBSlideProcessor from '@app/services/export/ppt/slides/PowerPointOfferBSlideProcessor';
import PowerPointOfferCSlideProcessor from '@app/services/export/ppt/slides/PowerPointOfferCSlideProcessor';
import PowerPointOfferDSlideProcessor from '@app/services/export/ppt/slides/PowerPointOfferDSlideProcessor';
import PowerPointOfferESlideProcessor from '@app/services/export/ppt/slides/PowerPointOfferESlideProcessor';
import PowerPointChapterOverViewSlideProcessor
	from '@app/services/export/ppt/slides/PowerPointChapterOverviewSlideProcessor';
import IRoutePricesSlideData from '@app/types/data/slides/IRoutePricesSlideData';
import ITravelPassSlideData from '@app/types/data/slides/ITravelPassSlideData';
import ITravelPassVipSlideData from '@app/types/data/slides/ITravelPassVipSlideData';
import ISkiRegionSlideData from '@app/types/data/slides/ISkiRegionSlideData';
import ICustomSlideData from '@app/types/data/slides/ICustomSlideData';
import IThankYouSlideData from '@app/types/data/slides/IThankYouSlideData';
import ICooperationSlideData from '@app/types/data/slides/ICooperationSlideData';
import ISingleMediaSlideData from '@app/types/data/slides/ISingleMediaSlideData';
import IHighlightSlideData from '@app/types/data/slides/IHighlightSlideData';
import ITrainRouteSlideData from '@app/types/data/slides/ITrainRouteSlideData';
import IHomeSlideData from '@app/types/data/slides/IHomeSlideData';
import IWelcomeSlideData from '@app/types/data/slides/IWelcomeSlideData';
import IOfferASlideData from '@app/types/data/slides/IOfferASlideData';
import IOfferBSlideData from '@app/types/data/slides/IOfferBSlideData';
import IOfferCSlideData from '@app/types/data/slides/IOfferCSlideData';
import IOfferDSlideData from '@app/types/data/slides/IOfferDSlideData';
import IOfferESlideData from '@app/types/data/slides/IOfferESlideData';
import IChapterOverviewSlideData from '@app/types/data/slides/IChapterOverviewSlideData';

export default class PowerPointExportService {
	public async createPresentation(slideViews: ISlideView[], presentationName: string, author?: string): Promise<void> {
		console.log('[PowerPointExportService] Gathering PPTX data...');

		const presentation = new PowerPointPresentation();

		presentation.defineLayout({
			name: 'CUSTOM_LAYOUT',
			width: presentationDimensions.w,
			height: presentationDimensions.h
		});

		presentation.layout = 'CUSTOM_LAYOUT';

		if (author) {
			presentation.author = author;
		}

		presentation.company = 'Jungfraubahnen Management AG';
		presentation.title = presentationName;

		slideViews.forEach((slideView: ISlideView): void => {
			const processor: PowerPointSlideProcessor<ISlideData> = PowerPointExportService.getPowerPointSlideTransformByView(
				slideView,
				presentation
			);
			presentation.registerSlideIds(processor.getSlideIdsForSlideData());
		});

		// eslint-disable-next-line no-restricted-syntax
		for (const slideView of slideViews) {

			const processor: PowerPointSlideProcessor<ISlideData> = PowerPointExportService.getPowerPointSlideTransformByView(slideView, presentation);

			// eslint-disable-next-line no-await-in-loop
			await processor.processSlideData();
		}

		console.log('[PowerPointExportService] Generating PPTX...');
		console.time('[PowerPointExportService] Done.');

		await presentation.writeFile({ fileName: `${presentationName}.pptx` });
		console.timeEnd('[PowerPointExportService] Done.');
	}

	private static getPowerPointSlideTransformByView(
		slideView: ISlideView<ISlideData>,
		presentation: PowerPointPresentation
	): PowerPointSlideProcessor<ISlideData> {
		switch (slideView.viewComponent) {
			case 'WelcomeSlide':
				return new PowerPointWelcomeSlideProcessor(<IWelcomeSlideData>slideView.data, presentation);
			case 'HomeSlide':
				return new PowerPointHomeSlideProcessor(<IHomeSlideData>slideView.data, presentation);
			case 'TrainRouteSlide':
				return new PowerPointTrainRouteSlideProcessor(<ITrainRouteSlideData>slideView.data, presentation);
			case 'HighlightSlide':
				return new PowerPointHighlightSlideProcessor(<IHighlightSlideData>slideView.data, presentation);
			case 'SingleMediaSlide':
				return new PowerPointSingleMediaSlideProcessor(<ISingleMediaSlideData>slideView.data, presentation);
			case 'CooperationSlide':
				return new PowerPointCooperationSlideProcessor(<ICooperationSlideData>slideView.data, presentation);
			case 'ThankYouSlide':
				return new PowerPointThankYouSlideProcessor(<IThankYouSlideData>slideView.data, presentation);
			case 'CustomSlide':
				return new PowerPointCustomSlideProcessor(<ICustomSlideData>slideView.data, presentation);
			case 'SkiRegionSlide':
				return new PowerPointSkiRegionSlideProcessor(<ISkiRegionSlideData>slideView.data, presentation);
			case 'TravelPassSlide':
				return new PowerPointTravelPassSlideProcessor(<ITravelPassSlideData>slideView.data, presentation);
			case 'TravelPassVipSlide':
				return new PowerPointTravelPassVipSlideProcessor(<ITravelPassVipSlideData>slideView.data, presentation);
			case 'RoutePricesSlide':
				return new PowerPointRoutePricesSlideProcessor(<IRoutePricesSlideData>slideView.data, presentation);
			case 'OfferASlide':
				return new PowerPointOfferASlideProcessor(<IOfferASlideData>slideView.data, presentation);
			case 'OfferBSlide':
				return new PowerPointOfferBSlideProcessor(<IOfferBSlideData>slideView.data, presentation);
			case 'OfferCSlide':
				return new PowerPointOfferCSlideProcessor(<IOfferCSlideData>slideView.data, presentation);
			case 'OfferDSlide':
				return new PowerPointOfferDSlideProcessor(<IOfferDSlideData>slideView.data, presentation);
			case 'OfferESlide':
				return new PowerPointOfferESlideProcessor(<IOfferESlideData>slideView.data, presentation);
			case 'ChapterOverviewSlide':
				return new PowerPointChapterOverViewSlideProcessor(<IChapterOverviewSlideData>slideView.data, presentation);
			default:
				return new PowerPointSlideProcessorStub(slideView.data, presentation);
		}
	}
}
